import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import InvestorInvestmentApproval from '../Models/Approvals/InvestorInvestmentApproval';
import GenerateTermsAndConditionsCommand from "../Models/InvestorInvestments/Commands/GenerateTermsAndConditionsCommand";

export interface IInvestorInvestmentApprovalApiClient {

    /**
     * Gets the approval with the specified id
     * @param approvalId The id of the approval to fetch
     * @returns The  with the given id
     */
    getApproval(approvalId: number): AxiosPromise<Model.PlainTrackedObject<InvestorInvestmentApproval>>;

    /**
     * Creates a new Approval
     * @param investorInvestmentId The id of the investor investment for which the approval should be created
     * @returns The newly created
     */
    create(investorInvestmentId: number): AxiosPromise<Model.PlainTrackedObject<InvestorInvestmentApproval>>;

    /**
     * Saves an Approval
     * @param approval The Approval to save
     * @returns The saved
     */
    save(approval: Model.PartialPlainObject<InvestorInvestmentApproval>): AxiosPromise<Model.PlainTrackedObject<InvestorInvestmentApproval>>;

    /**
     * Activates/Deactivates the Approval with the given id
     * @param approvalId The id of the Approval to activate/deactivate
     * @returns A Task
     */
    toggleActivation(approvalId: number): AxiosPromise<Model.PlainTrackedObject<Model.UserEvent>>;

    generateTermsAndConditions(command: Model.PartialPlainObject<GenerateTermsAndConditionsCommand>): AxiosPromise<Blob>;

    getUploadTermsAndConditionsUrl(): string;

    /**
     * Completes the Approval with the given id
     * @param approvalId The id of the Approval to complete
     * @returns A Task
     */
    complete(approvalId: number): AxiosPromise<Model.PlainTrackedObject<Model.UserEvent>>;

    /**
     * Deletes the Approval with the given id
     * @param approvalId The id of the Approval to delete
     * @returns A Task
     */
    delete(approvalId: number): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class InvestorInvestmentApprovalApiClient extends Data.ApiClientBase implements IInvestorInvestmentApprovalApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/investor-investment-approval`);
    }

    public getApproval(approvalId: number): AxiosPromise<Model.PlainTrackedObject<InvestorInvestmentApproval>> {
        return this.axios.get(`${this.apiPath}/${approvalId}`);
    }

    public create(investorInvestmentId: number): AxiosPromise<Model.PlainTrackedObject<InvestorInvestmentApproval>> {
        return this.axios.post(`${this.apiPath}/create/${investorInvestmentId}`);
    }

    public save(approval: Model.PartialPlainObject<InvestorInvestmentApproval>): AxiosPromise<Model.PlainTrackedObject<InvestorInvestmentApproval>> {
        return this.axios.post(`${this.apiPath}/save`, approval);
    }

    public toggleActivation(approvalId: number): AxiosPromise<Model.PlainTrackedObject<Model.UserEvent>> {
        return this.axios.post(`${this.apiPath}/toggle-activation/${approvalId}`);
    }

    public generateTermsAndConditions(command: Model.PartialPlainObject<GenerateTermsAndConditionsCommand>): AxiosPromise<Blob> {
        return this.axios.post(`${this.apiPath}/generate-terms-and-conditions`, command, { responseType: "blob" });
    }

    public getUploadTermsAndConditionsUrl(): string {
        return `${this.apiPath}/upload-terms-and-conditions`;
    }

    public complete(approvalId: number): AxiosPromise<Model.PlainTrackedObject<Model.UserEvent>> {
        return this.axios.post(`${this.apiPath}/complete/${approvalId}`);
    }

    public delete(approvalId: number): AxiosPromise {
        return this.axios.delete(`${this.apiPath}/${approvalId}`);
    }

    // Client only properties / methods
}